import React, { useEffect } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Divider,
  Chip,
  Skeleton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Link } from "react-router-dom";

import customersIcon from "../../../assets/images/icon/customers.svg";
import sitesIcon from "../../../assets/images/icon/sites.svg";
import licensesIcon from "../../../assets/images/icon/licenses.svg";
import search from "../../../assets/images/icon/search.svg";
import { PROFILE_TYPE } from "../../../helper/globalConstant";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";
import PersonPinIcon from "@mui/icons-material/PersonPin";
import { SelectedDistributorPartner } from "../../sharedComponent/SelectedDistributorPartner";
import {SolidStateDisk} from "@icon-park/react";
import {IconFileCertificate} from "@tabler/icons-react"
import { IconSearch } from '@tabler/icons-react';
import {BuildingFour} from "@icon-park/react"
export interface CustomerOverviewStatsProps {
  totalDistributorOverview: any;
  totalPartnerOverview: any;
  totalCustomerOverview: any;
  siteDeviceOverview: any;
  totalLicenseOverview: any;
  setSearchText: (text: string) => void;
  debouncedSearch: (text: string) => void;
  userPriority: any;
  activeView: "distributors" | "partners" | "customers";
  selectedDistributor?: any;
  selectedPartner?: any;
  loadingDistributors?: boolean;
  loadingPartners?: boolean;
  loadingCustomers?: boolean;
  loadingSites?: boolean;
  loadingLicenses?: boolean;
}

const StatCard = ({ title, icon, total, stats, show = true, loading = false }: any) => {
  if (!show) return null;
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: "grow" }}>
      <Card
        sx={{
          backgroundColor: "#1D292E",
          height: "100%",
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 30px rgba(23,178,235,0.15)",
          },
        }}
      >
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Box
            sx={{
              display: "flex",
              borderBottom: "1px solid rgba(90,95,96,0.5)",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 1,
              mb: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#E2E3E3",
                fontSize: "13px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {icon}
              {title}
            </Typography>
            {loading ? (
              <Skeleton
                variant="rectangular"
                width={34}
                height={34}
                sx={{
                  borderRadius: "17px",
                  bgcolor: "rgba(23,178,235,0.1)",
                }}
              />
            ) : (
              <Typography
                variant="h4"
                sx={{
                  color: "#17B2EB",
                  minWidth: "34px",
                  padding: "0 6px",
                  height: "34px",
                  borderRadius: "17px",
                  fontSize: "15px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(23,178,235,0.1)",
                }}
              >
                {total || 0}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            {stats.map((stat: any, index: number) => (
              <Typography
                key={index}
                variant="body2"
                sx={{
                  color: "#B4B7B8",
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: loading ? "rgba(180, 183, 184, 0.2)" : stat.color,
                  }}
                />{" "}
                {loading ? (
                  <Skeleton
                    variant="text"
                    width={60}
                    sx={{ bgcolor: "rgba(180, 183, 184, 0.2)" }}
                  />
                ) : (
                  `${stat.label}: ${stat.value || 0}`
                )}
              </Typography>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

export const CustomerOverviewStats: React.FC<CustomerOverviewStatsProps> = ({
  totalDistributorOverview,
  totalPartnerOverview,
  totalCustomerOverview,
  siteDeviceOverview,
  totalLicenseOverview,
  setSearchText,
  debouncedSearch,
  userPriority,
  activeView,
  selectedDistributor,
  selectedPartner,
  loadingDistributors = false,
  loadingPartners = false,
  loadingCustomers = false,
  loadingSites = false,
  loadingLicenses = false,
}) => {
  return (
    <>
      <Grid container spacing={2.5} mb={2}>
        {/* Top Selection Info Bar */}
        <SelectedDistributorPartner
          selectedDistributor={selectedDistributor}
          selectedPartner={selectedPartner}
        />

        {/* Stat Cards */}
        <StatCard
          show={activeView === "distributors"}
          title="Total Distributors"
          icon={
            <SupervisedUserCircleIcon sx={{ color: "#0699CE !important" }} />
          }
          total={totalDistributorOverview?.total}
          loading={loadingDistributors}
          stats={[
            {
              label: "Active",
              value: totalDistributorOverview?.active,
              color: "#4CAF50",
            },
            {
              label: "Suspended",
              value: totalDistributorOverview?.suspended,
              color: "#FFC107",
            },
          ]}
        />

        <StatCard
          show={activeView === "distributors" || activeView === "partners"}
          title="Total Partners"
          icon={<PersonPinIcon sx={{ color: "#0699CE !important" }} />}
          total={totalPartnerOverview?.total}
          loading={loadingPartners}
          stats={[
            {
              label: "Active",
              value: totalPartnerOverview?.active,
              color: "#4CAF50",
            },
            {
              label: "Suspended",
              value: totalPartnerOverview?.suspended,
              color: "#FFC107",
            },
          ]}
        />

        <StatCard
          show={true}
          title="Total Customers"
          // icon={<img src={customersIcon} alt="" width={20} height={20} />}
          icon ={<BuildingFour theme="outline" size="20" fill="#0699CE" strokeWidth={2.8}/>}
          total={totalCustomerOverview?.total}
          loading={loadingCustomers}
          stats={[
            {
              label: "Active",
              value: totalCustomerOverview?.active,
              color: "#4CAF50",
            },
            {
              label: "Suspended",
              value: totalCustomerOverview?.suspended,
              color: "#FFC107",
            },
          ]}
        />

        <StatCard
          show={true}
          title="Sites / Devices"
          // icon={<img src={sitesIcon} alt="" width={20} height={20} />}
          icon={<SolidStateDisk theme="outline" size="20" fill="#0699CE" strokeWidth={2.8}/>}
          total={siteDeviceOverview?.total}
          loading={loadingSites}
          stats={[
            {
              label: "Registered",
              value: siteDeviceOverview?.active,
              color: "#4CAF50",
            },
            {
              label: "Suspended",
              value: siteDeviceOverview?.suspended,
              color: "#FFC107",
            },
            {
              label: "Unregistered",
              value: siteDeviceOverview?.pending,
              color: "#F44336",
            },
          ]}
        />

        <StatCard
          show={true}
          title="Total Licenses"
          // icon={<img src={licensesIcon} alt="" width={20} height={20} />}
          icon={<IconFileCertificate stroke={1.4} size={20} color="#0699CE"/>}
          total={totalLicenseOverview?.total}
          loading={loadingLicenses}
          stats={[
            {
              label: "Active",
              value: totalLicenseOverview?.active,
              color: "#4CAF50",
            },
            {
              label: "Available",
              value: totalLicenseOverview?.available,
              color: "#4CAF50",
            },
            {
              label: "Expiring",
              value: totalLicenseOverview?.expiring,
              color: "#F44336",
            },
            {
              label: "Expired",
              value: totalLicenseOverview?.expired,
              color: "#F44336",
            },
            ...(userPriority === PROFILE_TYPE.SUPER_ADMIN || userPriority === PROFILE_TYPE.DISTRIBUTOR
              ? []
              : [{
                  label: "Suspended",
                  value: totalLicenseOverview?.suspended,
                  color: "#FFC107",
                }]),
          ]}
        />
      </Grid>

      {activeView === "customers" && (
        <Grid container spacing={2} mb={5} mt={5}>
          <Grid size={{ xs: 12, md: 6 }}>
            <div
              style={{
                height: 41,
                padding: 12,
                borderRadius: 30,
                border: "1px solid #5a5f60",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "#1F2F34",
                width: "100%",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                {/* <img src={search} style={{ cursor: "pointer" }} alt="search" /> */}
                <IconSearch stroke={1.4} color="#A5A8A9" style={{ cursor: "pointer" }} aria-label="search"/>
                <input
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    debouncedSearch(e.target.value);
                  }}
                  placeholder={"Search by customer name or site name or UUID"}
                  style={{
                    background: "#1F2F34",
                    border: "none",
                    padding: 0,
                    margin: 0,
                    marginLeft: 8,
                    color: "#FFF",
                    width: "100%",
                  }}
                />
              </div>
            </div>
          </Grid>
          {(userPriority === PROFILE_TYPE.INTEGRATOR ||
            userPriority === PROFILE_TYPE.INTEGRATOR_TECHNICIAN_G1 ||
            userPriority === PROFILE_TYPE.INTEGRATOR_TECHNICIAN_G2) && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#17B2EB",
                  color: "#1F2F34",
                  marginLeft: 2,
                  textTransform: "none",
                  borderRadius: "30px !important",
                  fontSize: "14px",
                  fontWeight: "normal",
                  "&:hover": { backgroundColor: "#fff" },
                  float: "right",
                }}
              >
                <Link
                  to="/customer/new"
                  style={{ textDecoration: "none", color: "black" }}
                >
                  + Register Customer{" "}
                </Link>
              </Button>
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
};
